// Trade analysis maths (UI polish, 22 Oct 2026) - DISPLAY ONLY, never used for an order, a target or a risk gate.
//
// optionExpiryPayoff: the P&L AT EXPIRY of a set of option legs held from their CONFIRMED average fill prices
// (intrinsic value only, before charges). Exact at expiry; says nothing about the P&L before expiry.
// futuresSpreadScenario: the P&L of the held futures levels if both legs are closed at a given spread
// (FAR − NEAR, the engine's own sign: dashboard_engine.current_spread = sell − buy), gross, before charges -
// the same per-leg formula as dashboard_engine.leg_pnl.

const intrinsic = (type, strike, s) => (type === "CE" ? Math.max(0, s - strike) : Math.max(0, strike - s));

/** legs: [{side: "BUY"|"SELL", type: "CE"|"PE", strike, price, units}] - `units` = quantity × multiplier held. */
export function legPnlAt(leg, s) {
  const v = intrinsic(leg.type, Number(leg.strike), s);
  const sign = leg.side === "BUY" ? 1 : -1;
  return sign * (v - Number(leg.price)) * Number(leg.units);
}

export const payoffAt = (legs, s) => legs.reduce((a, l) => a + legPnlAt(l, s), 0);

/** The curve over a range that contains every strike and `spot`, its breakevens, and the max profit / loss with
 *  "unlimited" where the payoff keeps rising / falling past the last strike (net long / short calls). */
export function optionExpiryPayoff(legs, { spot = null, points = 121 } = {}) {
  const known = (v) => v !== null && v !== undefined && v !== "" && Number.isFinite(Number(v));
  const usable = (legs || []).filter((l) => l && ["CE", "PE"].includes(l.type) && known(l.strike) && known(l.price) && Number(l.units) > 0);
  if (!usable.length) return null;
  const strikes = [...new Set(usable.map((l) => Number(l.strike)))].sort((a, b) => a - b);
  const ref = [...strikes, ...(Number.isFinite(Number(spot)) && spot ? [Number(spot)] : [])];
  const lo0 = Math.min(...ref), hi0 = Math.max(...ref);
  // wide enough to hold every breakeven: they sit at most (total premium per unit) beyond the outer strikes
  const premium = usable.reduce((a, l) => a + Math.abs(Number(l.price)), 0);
  const pad = Math.max((hi0 - lo0) * 0.6, hi0 * 0.06, premium * 1.25, 1);
  const lo = Math.max(0, lo0 - pad), hi = hi0 + pad;
  const xs = new Set(strikes);
  for (let i = 0; i < points; i += 1) xs.add(Math.round((lo + ((hi - lo) * i) / (points - 1)) * 100) / 100);
  const sorted = [...xs].sort((a, b) => a - b);
  const curve = sorted.map((s) => ({ s, pnl: Math.round(payoffAt(usable, s) * 100) / 100 }));
  const breakevens = [];
  for (let i = 1; i < curve.length; i += 1) {
    const a = curve[i - 1], b = curve[i], c = curve[i + 1];
    if ((a.pnl < 0 && b.pnl > 0) || (a.pnl > 0 && b.pnl < 0)) breakevens.push(Math.round((a.s + ((b.s - a.s) * -a.pnl) / (b.pnl - a.pnl)) * 100) / 100);
    else if (b.pnl === 0 && c && a.pnl * c.pnl < 0) breakevens.push(b.s);
  }
  // slope past the highest strike: + net long calls, - net short calls (puts are worth 0 there)
  const callSlope = usable.filter((l) => l.type === "CE").reduce((a, l) => a + (l.side === "BUY" ? 1 : -1) * Number(l.units), 0);
  const vals = curve.map((p) => p.pnl);
  const atZero = payoffAt(usable, 0);
  const maxProfit = callSlope > 0 ? null : Math.max(...vals, atZero);
  const maxLoss = callSlope < 0 ? null : Math.min(...vals, atZero);
  return { curve, strikes, breakevens, maxProfit, maxLoss, unlimitedProfit: callSlope > 0, unlimitedLoss: callSlope < 0,
    spotPnl: Number.isFinite(Number(spot)) && spot ? Math.round(payoffAt(usable, Number(spot)) * 100) / 100 : null };
}

/** levels held now (OPEN / ENTERING with fills): [{sell_entry_price, buy_entry_price, sell_quantity, buy_quantity}].
 *  Returns null when a level's legs are unequal (then the P&L is not a function of the spread alone). */
export function futuresSpreadScenario(levels, currentSpread) {
  const held = (levels || []).filter((l) => l && l.sell_entry_price != null && l.buy_entry_price != null
    && Number(l.sell_quantity) > 0 && Number(l.buy_quantity) > 0);
  if (!held.length) return null;
  if (held.some((l) => Number(l.sell_quantity) !== Number(l.buy_quantity))) return { unbalanced: true };
  const qty = held.reduce((a, l) => a + Number(l.sell_quantity), 0);
  // Σ q × (entry spread − X): the engine's leg_pnl summed over both legs, closed at spread X
  const entryWeighted = held.reduce((a, l) => a + Number(l.sell_quantity) * (Number(l.sell_entry_price) - Number(l.buy_entry_price)), 0);
  const avgEntry = entryWeighted / qty;
  const at = (x) => Math.round((entryWeighted - qty * x) * 100) / 100;
  const cur = Number.isFinite(Number(currentSpread)) ? Number(currentSpread) : null;
  const span = Math.max(Math.abs(avgEntry) * 0.5, Math.abs((cur ?? avgEntry) - avgEntry) * 2, 1);
  const lo = Math.min(avgEntry, cur ?? avgEntry, 0) - span * 0.25, hi = Math.max(avgEntry, cur ?? avgEntry) + span;
  const pts = 61;
  const curve = Array.from({ length: pts }, (_, i) => { const s = Math.round((lo + ((hi - lo) * i) / (pts - 1)) * 100) / 100; return { s, pnl: at(s) }; });
  return { qty, levels: held.length, avgEntry, breakeven: avgEntry, atCurrent: cur != null ? at(cur) : null, atConvergence: at(0),
    perPoint: qty, curve, current: cur };
}
